/* eslint-disable @typescript-eslint/no-explicit-any */
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FeatureFlags } from '@gen2/app/components/feature-flags/feature-flags';
import { LiveFormType } from '@gen2/app/invites/send-invites/store';
import { useAuth } from '@gen2/hooks';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { StyledSwitch } from '@nx-fe/components';
import React, { useState } from 'react';
import { AnnotationTypeEnum, TAssignee } from '../utils/types';
import { DraggableAnnotation } from './draggableAnnotations/draggableAnnotations';
import {
  StyledFormTitle,
  StyledLabel,
  StyledSidePanelBox,
  StyledTypography,
} from './sidePanel.styled';

interface SidePanelProps {
  users: TAssignee[];
  handleChange?: (e: any) => void | undefined;
  currSignee: TAssignee;
  onDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    type: string,
    dateFormat?: string,
  ) => void;
  onDragEnd: (e: any) => void;
  isTemplate?: boolean;
  type: LiveFormType;
  checked?: boolean;
  handleCheck?: (e: any) => void;
  onAddContact?: () => void;
  isSignatureTypeEnabled?: boolean;
  onSignatureTypeChange?: (enabled: boolean) => void;
}

// Date field format options
const DATE_FORMAT_OPTIONS = [
  { value: 'US', label: 'US (MM/DD/YYYY)' },
  { value: 'EU', label: 'European (DD/MM/YYYY)' },
  { value: 'ISO', label: 'ISO (YYYY-MM-DD)' },
];

// Custom component for date field with format selector
const DateFieldWithFormatSelector = ({
  onDragStart,
  onDragEnd,
  isDraggable = true,
}: {
  onDragStart?: (
    event: React.DragEvent<HTMLDivElement>,
    type: string,
    dateFormat?: string,
  ) => void;
  onDragEnd?: any;
  isDraggable?: boolean;
}) => {
  const [selectedFormat, setSelectedFormat] = useState('US');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDragStart = async (e: React.DragEvent<HTMLDivElement>) => {
    if (isDraggable && onDragStart) {
      // Pass the selected format as additional parameter
      await onDragStart(e, AnnotationTypeEnum.DateAndTimeField, selectedFormat);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (isDraggable && onDragEnd) {
      onDragEnd(e, AnnotationTypeEnum.DateAndTimeField);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main draggable date field */}
      <DraggableAnnotation
        type={AnnotationTypeEnum.DateAndTimeField}
        label="Date Field"
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        isDraggable={isDraggable}
      />

      {/* Format selector */}
      <Box sx={{ mt: 1, pl: 2, pr: 2 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            color: 'text.secondary',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          {isExpanded ? '▼' : '▶'} Format:{' '}
          {
            DATE_FORMAT_OPTIONS.find((opt) => opt.value === selectedFormat)
              ?.label
          }
        </Button>

        {isExpanded && (
          <Box sx={{ mt: 1 }}>
            <Select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value as string)}
              size="small"
              fullWidth
              sx={{ fontSize: '0.75rem' }}
            >
              {DATE_FORMAT_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const SidePanel = ({
  users,
  handleChange,
  currSignee,
  onDragStart,
  onDragEnd,
  isTemplate = false,
  type,
  checked = false,
  handleCheck,
  onAddContact,
  isSignatureTypeEnabled = false,
  onSignatureTypeChange,
}: SidePanelProps) => {
  const { featureFlags } = useAuth();

  return (
    <StyledSidePanelBox>
      <Stack spacing={5}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <StyledSwitch
            color="primary"
            checked={checked}
            onChange={handleCheck}
            data-cy="switch"
          />
          <StyledTypography>
            {' '}
            {checked
              ? 'Toggle off to resume form designing'
              : 'Toggle on to fill out and sign the form'}{' '}
          </StyledTypography>
        </Box>
        <Box>
          <StyledFormTitle
            style={{ paddingBottom: '20px', paddingTop: '10px' }}
          >
            Add Fields<span> | Drag & drop</span>
          </StyledFormTitle>
          <DraggableAnnotation
            type={AnnotationTypeEnum.TextField}
            label="Text Field"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          <DraggableAnnotation
            type={AnnotationTypeEnum.CheckBox}
            label="Check Box"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          <DraggableAnnotation
            type={AnnotationTypeEnum.RadioButton}
            label="Radio Button"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          {!featureFlags.live_form_listbox_removal && (
            <DraggableAnnotation
              type={AnnotationTypeEnum.ListBox}
              label="List Box"
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isDraggable={!checked}
            />
          )}
          <DraggableAnnotation
            type={AnnotationTypeEnum.ComboBox}
            label="Combo Box"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          <DateFieldWithFormatSelector
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
        </Box>
        <Box>
          <StyledFormTitle
            style={{ paddingBottom: '20px', paddingTop: '10px' }}
          >
            Signatures<span> | Drag & drop</span>
          </StyledFormTitle>
          {isTemplate && type === 'multi' && (
            <Box
              sx={{
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                variant="text"
                color="secondary"
                onClick={onAddContact}
                sx={{ color: '#0B487a' }}
                disabled={users.length >= 10 || checked}
              >
                Add Contact Placeholder
              </Button>
              <Tooltip
                title="You can add up to 10 contact placeholders which will be available in the dropdown menu"
                placement="top"
              >
                <FontAwesomeIcon icon={faCircleInfo} />
              </Tooltip>
            </Box>
          )}
          {type === 'multi' && handleChange && (
            <Box paddingBottom={2}>
              <StyledLabel>Placing fields for:</StyledLabel>
              <TextField
                id="assignee-select"
                inputProps={{
                  'data-cy': 'assignee-select',
                }}
                select
                value={currSignee.email}
                fullWidth
                onChange={handleChange}
                disabled={checked}
              >
                {users.map((option: TAssignee) => (
                  <MenuItem key={option.id} value={option.email}>
                    {`${option.first_name} ${option.last_name}`}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          <DraggableAnnotation
            type={AnnotationTypeEnum.SIGNATURE}
            label="Signature"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          <FeatureFlags restriction="live_form_signature_with_date">
            <DraggableAnnotation
              type={AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED}
              label="Signature with Date Signed"
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isDraggable={!checked}
            />
          </FeatureFlags>
          <DraggableAnnotation
            type={AnnotationTypeEnum.INITIAL}
            label="Initial"
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDraggable={!checked}
          />
          <Box sx={{ mt: 2, ml: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSignatureTypeEnabled}
                  onChange={(e) => onSignatureTypeChange?.(e.target.checked)}
                  disabled={checked}
                />
              }
              label="Allow type signatures"
            />
          </Box>
        </Box>
      </Stack>
    </StyledSidePanelBox>
  );
};

export default SidePanel;
